import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LeadService } from 'src/app/private/services/lead.service';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-add-pipeline',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
  templateUrl: './add-pipeline.component.html',
  styleUrls: ['./add-pipeline.component.scss']
})
export class AddPipelineComponent {
  @Input() formAction: string = 'Add';
  @Input() editPipelineData: any = null;
  @Input() pipelineType: string = 'lead';
  @Output() closeDialog = new EventEmitter<void>();
  @Output() reload = new EventEmitter<void>();

  pipelineForm!: FormGroup;
  loading: boolean = false;
  submitted: boolean = false;

  // Default stages that cannot be removed (for lead type)
  defaultLeadStages = [
    { stage_name: 'New Lead', score: 15, isDefault: true },
    { stage_name: 'Assigned', score: 25, isDefault: true },
    { stage_name: 'Contact Attempted', score: 35, isDefault: true },
    { stage_name: 'Contacted', score: 50, isDefault: true },
    { stage_name: 'Interested', score: 65, isDefault: true },
    { stage_name: 'Follow-up Required', score: 60, isDefault: true },
    { stage_name: 'Appointment Scheduled', score: 85, isDefault: true },
    { stage_name: 'Visited', score: 95, isDefault: true },
    { stage_name: 'Converted', score: 100, isDefault: true },
    { stage_name: 'Lost', score: 0, isDefault: true }
  ];

  constructor(
    private fb: FormBuilder,
    private api: LeadService,
    private localStorage: LocalStorageService,
    private toast: ToastrService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.pipelineForm = this.fb.group({
      pipeline_name: ['', [Validators.required, Validators.minLength(3)]],
      set_default: [false],
      stages: this.fb.array([])
    });

    if (this.formAction === 'Edit' && this.editPipelineData) {
      this.populateForm();
    } else {
      // Add default stages for new pipeline
      if (this.pipelineType === 'lead') {
        this.defaultLeadStages.forEach(stage => {
          this.addStage(stage);
        });
      }
    }
  }

  populateForm(): void {
    this.pipelineForm.patchValue({
      pipeline_name: this.editPipelineData.pipeline_name,
      set_default: this.editPipelineData.set_default
    });

    if (this.editPipelineData.stages && this.editPipelineData.stages.length > 0) {
      this.editPipelineData.stages.forEach((stage: any) => {
        const isDefault = this.isDefaultStage(stage.stage_name);
        this.addStage({
          ...stage,
          isDefault
        });
      });
    }
  }

  isDefaultStage(stageName: string): boolean {
    if (this.pipelineType !== 'lead') return false;
    return this.defaultLeadStages.some(s => s.stage_name === stageName);
  }

  get stages(): FormArray {
    return this.pipelineForm.get('stages') as FormArray;
  }

  createStageFormGroup(stage?: any): FormGroup {
    return this.fb.group({
      stage_id: [stage?.stage_id || null],
      stage_name: [stage?.stage_name || '', Validators.required],
      score: [stage?.score || 0, [Validators.required, Validators.min(0), Validators.max(100)]],
      isDefault: [stage?.isDefault || false]
    });
  }

  addStage(stage?: any): void {
    this.stages.push(this.createStageFormGroup(stage));
  }

  addNewStage(): void {
    const newStage = this.createStageFormGroup();
    this.stages.insert(0, newStage);
  }

  removeStage(index: number): void {
    const stage = this.stages.at(index).value;
    if (!stage.isDefault) {
      this.stages.removeAt(index);
    } else {
      this.toast.warning('Default stages cannot be removed');
    }
  }

  getScorePercentage(score: number): string {
    return `${score}%`;
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.pipelineForm.invalid) {
      this.toast.error('Please fill all required fields correctly');
      return;
    }

    this.loading = true;

    const payload = {
      id: this.editPipelineData?.id,
      pipeline_name: this.pipelineForm.value.pipeline_name,
      pipeline_type: this.pipelineType,
      set_default: this.pipelineForm.value.set_default,
      hospital_id: Number(this.localStorage.getHospitalId()),
      client_id: Number(this.localStorage.getClientId()),
      stages: this.pipelineForm.value.stages.map((stage: any, index: number) => ({
        stage_id: stage.stage_id || index + 1,
        stage_name: stage.stage_name,
        score: stage.score
      }))
    };

    const apiCall = this.formAction === 'Edit' && this.editPipelineData?.id
      ? this.api.updatePipeline(payload)
      : this.api.createPipeline(payload);

    apiCall.subscribe(
      (response: any) => {
        this.loading = false;
        this.toast.success(response.message || `Pipeline ${this.formAction === 'Edit' ? 'updated' : 'created'} successfully`);
        this.reload.emit();
        this.onClose();
      },
      (error: any) => {
        this.loading = false;
        this.toast.error(error.error?.message || 'Something went wrong, Please try again later.');
      }
    );
  }

  onClose(): void {
    this.pipelineForm.reset();
    this.submitted = false;
    this.closeDialog.emit();
  }
}
